import React, { forwardRef, useEffect, useRef, useState } from "react";
import VirtualTable from "../ui/VirtualTable";
import ScrollWrap from "../ui/ScrollWrap";
import { ROUTES } from "../utils/Routes";
import { KBD } from "../utils/Keybindings";
import { listen } from "../hooks/Listener";
import ContextMenu from "../ui/ContextMenu";
import { ENUMS } from "../utils/Enums";
import { bm_date, drawPrice, drawQty, get14MonthLabels, getByKey, kbdEvent, showConfirm, showHelp } from "../utils/Utils";
import { drawPicker } from "../popovers/Picker";
import { showError, showMessage } from "../ui/Message";
import Nav from "../ui/Nav";
import Space from "../ui/Space";
import { createModal } from "../hooks/createModal";
import { pubCall } from "../utils/PubSub";
import Spin from "../ui/Spin";
import Label from "../ui/Label";
import { stock_structure } from "../data_structures/Stock";
import dayjs from "dayjs";


const chToPx = (_ch = 1, _addPad = true) => {
    let _pad = (_addPad) ? 10 : 0;
    return _ch * 9.3466 + _pad;
}

// placeholder data
const pd = {
    DATE: 946684800,
    QTY_S: "999",
    QTY_M: "9999",
    PRICE: "9999.99",
    TEXT: "—",
    AUTHOR: "PLACEHOLDER AUTHOR",
    TITLE: "PLACEHOLDER TITLE",
    CODE13: "XXXXXXXXXXXXX",
    CODE10: "XXXXXXXXXX",
    SUPPLIER: "TEXT",
    PUBLISHER: "TEXT",
    X: "X"
};


const availableColumns = [
    { value: 1, placeholder: pd.DATE, align: "left", label: "First Received", key: "first_recieved", title: "1stRcvd", width: chToPx(8), render: (e) => (e) ? bm_date(e) : "" },
    { value: 2, placeholder: pd.QTY_M, align: "right", label: "Available OH", key: "available_on_hand", title: "aOH", width: chToPx(4), render: (e) => drawQty(e) },
    { value: 3, placeholder: pd.QTY_M, align: "right", label: "Available OO", key: "available_on_order", title: "aOO", width: chToPx(4), render: (e) => drawQty(e) },
    { value: 4, placeholder: pd.AUTHOR, align: "left", label: "Author", key: "author", title: "Author", width: chToPx(25) },
    { value: 5, placeholder: pd.QTY_M, align: "right", label: "Backroom", key: "backroom", title: "Back", width: chToPx(4), render: (e) => drawQty(e) },
    { value: 6, placeholder: pd.TEXT, align: "left", label: "Binding", key: "binding", title: "Bd", width: chToPx(2) },
    { value: 7, placeholder: pd.TEXT, align: "left", label: "Class", key: "category_1", title: "Class (Main)", width: chToPx(17) },
    { value: 8, placeholder: pd.TEXT, align: "left", label: "Condition Code", key: "condition_code", title: "Cnd", width: chToPx(3) },
    { value: 9, placeholder: pd.CODE13, align: "left", label: "EAN", key: "ean", title: "EAN", width: chToPx(13) },
    { value: 10, placeholder: pd.QTY_M, align: "right", label: "Held", key: "held_point_of_sale", title: "Held", width: chToPx(4), render: (e) => drawQty(e) },
    { value: 11, placeholder: pd.CODE13, align: "left", label: "ISBN", key: "primary_code", title: "ISBN", width: chToPx(13) },
    { value: 12, placeholder: pd.CODE10, align: "left", label: "ISBN10", key: "isbn_10", title: "ISBN10", width: chToPx(10) },
    { value: 13, placeholder: pd.DATE, align: "left", label: "Last Received", key: "last_received", title: "LastRcvd", width: chToPx(8), render: (e) => (e) ? bm_date(e) : "" },
    { value: 14, placeholder: pd.DATE, align: "left", label: "Last Sold", key: "last_sold", title: "LastSold", width: chToPx(8) },
    { value: 15, placeholder: pd.QTY_S, align: "right", label: "Minimum Onhand", key: "minimum_onhand", title: "Min", width: chToPx(3), render: (e) => drawQty(e) },
    { value: 16, placeholder: pd.TEXT, align: "left", label: "Message (Notes)", key: "message", title: "Msg", width: chToPx(11) },
    { value: 17, placeholder: pd.TEXT, align: "left", label: "Multistore Onhand", key: "multistore_onhand", title: "Multistore O/H", width: chToPx(74) },
    { value: 18, placeholder: pd.TEXT, align: "left", label: "Notes", key: "notes", title: "Notes", width: chToPx(56) },
    { value: 19, placeholder: pd.QTY_M, align: "right", label: "On Hand", key: "total_on_hand", title: "O/H", width: chToPx(4), render: (e) => drawQty(e) },
    { value: 20, placeholder: pd.PRICE, align: "left", label: "Cost", key: "cost", title: "Cost", width: chToPx(15), render: (e) => drawPrice(e, true) },
    { value: 21, placeholder: pd.QTY_S, align: "right", label: "On Order", key: "on_order", title: "O/O", width: chToPx(3), render: (e) => drawQty(e) },
    { value: 22, placeholder: pd.QTY_S, align: "left", label: "Profit Margin", key: "profit_cost", title: "PM", width: chToPx(15), render: (e) => drawQty(e) },
    { value: 23, placeholder: pd.QTY_M, align: "right", label: "Pending On Order", key: "pending_on_order", title: "pOO", width: chToPx(4), render: (e) => drawQty(e) },
    { value: 24, placeholder: pd.QTY_M, align: "right", label: "Pending Return", key: "pending_return", title: "PRtn", width: chToPx(4), render: (e) => drawQty(e) },
    { value: 25, placeholder: pd.PRICE, align: "right", label: "Price", key: "price", title: "Price", width: chToPx(7), render: (e) => drawPrice(e, true) },
    { value: 26, placeholder: pd.DATE, align: "left", label: "Pubdate", key: "pubdate", title: "Pubdate", width: chToPx(8), render: (e) => (e) ? bm_date(e) : "" },
    { value: 27, placeholder: pd.PUBLISHER, align: "left", label: "Publisher", key: "publisher", title: "Publi", width: chToPx(5) },
    { value: 28, placeholder: pd.X, align: "left", label: "Pubstock", key: "pubstock", title: "P", width: chToPx(1) },
    // { value: 29, align: "left", label: "Quantity", key: "quantity", title: "Qty", width: chToPx(4)  },
    //{ value: 30, align: "left", label: "Received/Sold Months", key: "received_sold_months", title: get14MonthLabels(), width: chToPx(get14MonthLabels().length) },
    { value: 31, placeholder: pd.TEXT, align: "left", label: "Received Months", key: "received_months", title: get14MonthLabels(), width: chToPx(get14MonthLabels().length) },
    { value: 32, placeholder: pd.QTY_S, align: "right", label: "Special Order", key: "special_order", title: "S/O", width: chToPx(3), render: (e) => drawQty(e) },
    { value: 33, placeholder: pd.PRICE, align: "right", label: "Sales Price", key: "sell_price", title: "SalePr", width: chToPx(7), render: (e) => drawPrice(e, true) },
    { value: 34, placeholder: pd.TEXT, align: "left", label: "Section", key: "section", title: "Se", width: chToPx(2) },
    { value: 35, placeholder: pd.QTY_M, align: "right", label: "Sales (SLS)", key: "sales", title: "Sls", width: chToPx(4), render: (e) => drawQty(e) },
    { value: 36, placeholder: pd.X, align: "left", label: "Deleted/Delisted", key: "deleted_delisted", title: "D", width: chToPx(1) },
    { value: 37, placeholder: pd.TEXT, align: "left", label: "Sold Months", key: "sold_months", title: get14MonthLabels(), width: chToPx(get14MonthLabels().length) },
    { value: 38, placeholder: pd.PRICE, align: "left", label: "Sold Price", key: "sold_price", title: "SoldPr", width: chToPx(7), render: (e) => drawPrice(e, true) },
    { value: 39, placeholder: pd.TEXT, align: "left", label: "Class (Sub)", key: "category_2", title: "Class (Sub)", width: chToPx(8) },
    { value: 40, placeholder: pd.TEXT, align: "left", label: "Subject", key: "subject", title: "Subject", width: chToPx(9) },
    { value: 41, placeholder: pd.SUPPLIER, align: "left", label: "Supplier", key: "supplier_1", title: "Supl", width: chToPx(4) },
    { value: 42, placeholder: pd.TITLE, align: "left", label: "Title", key: "title", title: "Title", width: chToPx(40) },
    { value: 43, placeholder: pd.QTY_M, align: "right", label: "Total Received", key: "total_received", title: "TRcd", width: chToPx(5), render: (e) => drawQty(e) },
    { value: 44, placeholder: pd.QTY_M, align: "left", label: "Total Returned", key: "total_returned", title: "TRtn", width: chToPx(4), render: (e) => drawQty(e) },
    { value: 45, placeholder: pd.QTY_M, align: "right", label: "Total Sold", key: "total_sold", title: "Tsld", width: chToPx(5), render: (e) => drawQty(e) },
    { value: 46, placeholder: pd.CODE13, align: "left", label: "UPC", key: "secondary_code", title: "UPC", width: chToPx(15) },
    { value: 47, placeholder: pd.TEXT, align: "left", label: "Used Condition", key: "used_condition", title: "UsedC", width: chToPx(5) },
    { value: 48, placeholder: pd.QTY_S, align: "right", label: "Weight", key: "weight", title: "Wght", width: chToPx(5) },
    { value: 49, placeholder: pd.DATE, align: "left", label: "Enter Date", key: "entered", title: "Entered", width: chToPx(8), render: (e) => (e) ? bm_date(e) : "" },
    { value: 50, placeholder: pd.TEXT, align: "left", label: "Class", key: "class", title: "Class", width: chToPx(19) }
];

export const getDefaultColumns = () => {
    return [11, 2, 3, 25, 42, 4, 41, 7, 36, 28].map(item => {
        return getByKey(availableColumns, "value", item);
    })
}

export const parseCols = (_report, _highlight_live = false) => {
    if (_report.columns.length === 0) {
        return getDefaultColumns();
    } else {
        return [..._report.columns]
            .sort((a, b) => a.position - b.position)
            .map(item => {
                let _i = getByKey(availableColumns, "value", item.type);

                // base column object
                let col = {
                    ..._i,
                    width: Math.ceil(item.size),
                    column_id: item.column_id
                };

                return col;
            });
    }
};

export const getRowFromJSON = (_r, _columns) => {
    let _row = {};
    _r = JSON.parse(_r.values_json);
    _r.forEach((item, index) => {
        _row[_columns[index].key] = item.value;
    })
    return _row;
}



const ReportColumns = forwardRef(function ReportFilter(props, ref) {


    const { columnsChanged = false, setColumnsChanged = () => { }, controller = "", escape = () => { }, is_new = false, route = "", edit = false, columnView = false, onBlur = () => { }, onFocus = () => { }, report = {}, setReport = () => { }, reloadTrigger = false } = props;
    const [loading, setLoading] = useState(false)
    const scrollRef = useRef();
    const row_key = "column_id";




    const [columns, setColumns] = useState(parseCols(report));



    const [draftColumns, setDraftColumns] = useState([...columns]);
    const findIndex = () => draftColumns.findIndex(c => c.key === selectedColumn);


    const previewData = () => {

        return Array.from({ length: 10 }).map((item, index) => {
            const row = {};
            draftColumns.forEach(i => {
                row[i.key] = i.placeholder;
            })
            return row;
        })

    }




    const [data, setData] = useState([]);



    const saveColumns = () => {
        const payload = draftColumns.map((item, index) => ({
            position: index,
            column_id: item.column_id || "",
            display_name: item.title,
            size: Math.ceil(item.width),
            type: item.value
        }));

        setLoading(true);
        pubCall("report.purgeAndReplaceColumns", { report_id: report.report_id, columns: payload }, (_s, _r) => {
            setLoading(false);
            if (!_s) {
                showError(_r);
                return;
            }
            setColumns([...draftColumns]);
            setColumnsChanged(false);
            showMessage("Columns saved.");
            getPreview();
        }
        );
    };


    useEffect(() => {
        if (!edit) {
            if (columnsChanged) {
                setTimeout(() => {
                    showConfirm({
                        controller: controller,
                        route: window.bm_state.value,
                        description: <div style={{ "textAlign": "center" }}>The columns have been changed.<br /> Save changes?</div>,
                        title: "Save changes?",
                        callback: (e) => {
                            if (e) {
                                saveColumns()
                            }
                        }
                    })
                }, 0)
            }
        }
    }, [edit, columnsChanged])



    const getPreview = (_columns = draftColumns) => {

        if (is_new) {
            return;
        }

        setLoading(true);
        pubCall("report.getReportPreview", { report_id: report.report_id }, (_s, _r) => {
            if (_s) {
                let _d = _r.map(item => {
                    return getRowFromJSON(item, _columns);
                });
                setData(_d)
            } else {
                showError(_r);
            }
            setLoading(false);
        })
    }

    useEffect(() => {
        getPreview();
    }, [report.filters, reloadTrigger])

    // const purgeColumns = (_columns = columns) => {
    //     setLoading(true);
    //     let _cols = [..._columns]
    //     _cols = _cols.map((item, index) => {
    //         return ({
    //             "position": index,
    //             "column_id": (item?.[row_key]) ? item?.[row_key] : "",
    //             "display_name": item.title,
    //             "size": Math.ceil(item.width),
    //             "type": item.value
    //         })
    //     })

    //     pubCall("report.purgeAndReplaceColumns", { report_id: report.report_id, columns: _cols }, (_s, _r) => {
    //         if (_s) {
    //             //getPreview(_columns);
    //             setColumns(_columns);
    //             setLoading(false)
    //             //setReport(_r);
    //         } else {
    //             showError(_r);
    //         }
    //     })

    // }



    const [selectedColumn, setSelectedColumn] = useState(draftColumns[0].key || "");
    const [columnPosition, setColumnPosition] = useState(null);


    useEffect(() => {
        if (edit) {
            window.bm_state.value = ROUTES.REPORTS.COLUMNS
        }
    }, [edit])


    const next = () => {
        const i = findIndex();
        const _sc = draftColumns[(i + 1) % draftColumns.length]?.key;
        setSelectedColumn(_sc);
        if (scrollRef.current && columnRefs?.current && _sc) {
            scrollRef.current?.scrollToElement(columnRefs?.current[_sc]);
        }
    };

    const prev = () => {
        const i = findIndex();
        const _sc = draftColumns[(i - 1 + draftColumns.length) % draftColumns.length]?.key;
        setSelectedColumn(_sc);
        if (scrollRef.current && columnRefs?.current && _sc) {
            scrollRef.current?.scrollToElement(columnRefs?.current[_sc]);
        }
    };


    const debounceTimer = useRef(null);

    const incrementWidth = (delta = chToPx(1, false)) => {
        let updatedColumns;

        setDraftColumns(cols => {
            updatedColumns = cols.map(c => {
                if (c.key === selectedColumn) {
                    const minWidth = chToPx(c.title.length);
                    return {
                        ...c,
                        width: Math.max(c.width + delta, minWidth)
                    };
                }
                return c;
            });
            return updatedColumns;
        });

        setColumnsChanged(true);
    };

    const decrementWidth = (delta = chToPx(1, false)) => incrementWidth(-delta);

    const addColumn = (_newCol, _right = true) => {
        const newCol = availableColumns.find(item => item.value === _newCol);
        if (!newCol) { return; }
        const i = findIndex();
        const updatedColumns = [...draftColumns];
        if (_right) {
            updatedColumns.splice(i + 1, 0, newCol);
        } else {
            updatedColumns.splice(i, 0, newCol);
        }
        setDraftColumns(updatedColumns.filter(item => item.key !== "blank"));
        setSelectedColumn(newCol?.key)
        setColumnsChanged(true);
    };

    const changeColumn = (_newCol) => {
        const newCol = availableColumns.find(item => item.value === _newCol);
        if (!newCol) return;

        const updatedColumns = [...draftColumns];
        const existingIndex = updatedColumns.findIndex(c => c.key === newCol.key);
        const selectedIndex = updatedColumns.findIndex(c => c.key === selectedColumn);

        if (selectedIndex === -1) return;

        if (existingIndex !== -1 && existingIndex !== selectedIndex) {
            updatedColumns[selectedIndex] = newCol;
            updatedColumns.splice(existingIndex, 1);
        } else {
            updatedColumns[selectedIndex] = newCol;
        }

        setDraftColumns([...updatedColumns]);
        setColumnsChanged(true);
    };



    const resetColumns = () => {

        showConfirm({
            controller: controller,
            route: window.bm_state.value,
            description: <div style={{ "textAlign": "center" }}>Reset columns to system defaults?</div>,
            title: "Reset",
            callback: (e) => {
                if (e) {
                    setDraftColumns(getDefaultColumns())
                    setColumnsChanged(true);
                }
            }
        })
    }


    const removeColumn = () => {

        if (draftColumns.length <= 1) {
            showError({ error: "Must have at least 1 column." })
            return;
        }

        setDraftColumns(cols => {
            const i = findIndex();
            if (i === -1) return cols;
            const updated = cols.filter(c => c.key !== selectedColumn);
            if (!updated.length) {
                setSelectedColumn(null);
            } else {
                setSelectedColumn(updated[Math.max(0, i - 1)]?.key);
            }
            return updated;
        });

        setColumnsChanged(true);
        showMessage("Column removed.")
    };

    const shiftSwap = (left = true) => {
        const selectedIndex = draftColumns.findIndex(c => c.key === selectedColumn);
        if (selectedIndex === -1) return;

        const targetIndex = left
            ? selectedIndex - 1
            : selectedIndex + 1;

        if (targetIndex < 0 || targetIndex >= draftColumns.length) return;

        const targetKey = draftColumns[targetIndex].key;
        swapColumn(targetKey);
    };


    const swapColumn = (_key) => {

        const selectedIndex = draftColumns.findIndex(c => c.key === selectedColumn);
        const targetIndex = draftColumns.findIndex(c => c.key === _key);
        if (selectedIndex === -1 || targetIndex === -1) return;
        const updatedColumns = [...draftColumns];
        const [removed] = updatedColumns.splice(selectedIndex, 1);
        updatedColumns.splice(targetIndex, 0, removed);

        setDraftColumns([...updatedColumns]);
        setColumnsChanged(true);
    };

    const drawHelp = () => {

        showHelp({
            callback: () => { }, width: 280, route: route, title: "Modify the report layout", component: <div style={{ "padding": "10px" }}>
                <div className="shim" />
                <div className="shim" />
                <Label style={{ "textAlign": "left" }}>Select a column: <kbd>←</kbd> / <kbd>→</kbd></Label><div className="shim" />
                <Label style={{ "textAlign": "left" }}>Move the column: <kbd>Shift</kbd> + <kbd>←</kbd> / <kbd>→</kbd></Label><div className="shim" />
                <Label style={{ "textAlign": "left" }}>Change a column: <kbd>↑</kbd></Label><div className="shim" />
                <Label style={{ "textAlign": "left" }}>Increase the column width: <kbd>Ins</kbd></Label><div className="shim" />
                <Label style={{ "textAlign": "left" }}>Decrease the column width: <kbd>Del</kbd></Label><div className="shim" />
                <Label style={{ "textAlign": "left" }}>Insert a column: <kbd>I</kbd></Label><div className="shim" />
                <Label style={{ "textAlign": "left" }}>Delete the selected column: <kbd>D</kbd></Label><div className="shim" />
                <Label style={{ "textAlign": "left" }}>Reset columns to system defaults: <kbd>R</kbd></Label><div className="shim" />
            </div>
        });
    }

    const nav = [
        { text: "Help", key: KBD.H, action: (e) => drawHelp() },
        { text: "New Column", key: KBD.N, action: (e) => drawInsert() },
        { text: "Edit Column", key: KBD.E, action: (e) => drawSwap() },
        { text: "Delete Column", key: KBD.D, action: (e) => removeColumn() },
        { text: "Reset to Defaults", key: KBD.R, action: (e) => resetColumns() },



        { key: KBD.ARROW_LEFT, action: (e) => prev() },
        { key: KBD.ARROW_RIGHT, action: (e) => next() },
    ]

    useEffect(() => {

        if (!edit || loading || window.bm_state.value !== route) {
            return;
        }

        const actions = [
            { key: KBD.TAB, action: (e) => { } },
            { key: KBD.SHIFT_TAB, action: (e) => { } },
            { key: KBD.ENTER, action: (e) => { } },
            { key: KBD.D, action: (e) => removeColumn() },
            { key: KBD.ESCAPE, action: () => escape() },
            { key: KBD.SHIFT_ARROW_RIGHT, action: (e) => shiftSwap(false) },
            { key: KBD.SHIFT_ARROW_LEFT, action: (e) => shiftSwap(true) },
            { key: KBD.ARROW_UP, action: (e) => drawSwap() },
            { key: KBD.ARROW_DOWN, action: (e) => { } },
            { key: KBD.INSERT, action: (e) => incrementWidth() },
            { key: KBD.DELETE, action: (e) => decrementWidth() },

        ];
        const cleanup = listen(controller, route, actions.concat(nav), false, [KBD.TAB, KBD.ENTER]);
        return cleanup;
    }, [edit, selectedColumn, draftColumns, nav])


    const drawSwap = (_ref = columnRefs?.current[selectedColumn], _value) => {
        const i = findIndex();
        drawPicker({
            controller: controller,
            value: draftColumns[i].value, showSearch: true, title: "Change Column", closeable: true, mask_close: true, height: 280, ref: _ref, route: ROUTES.REPORTS.COLUMNS, close: () => { }, callback: (e) => {
                if (e) {
                    showMessage("Column added.")
                    changeColumn(e);
                }
            }, offset: [-370, 0],
            options: availableColumns.sort((a, b) => a.label.localeCompare(b.label))
        })
    }



    const drawInsert = (_ref = columnRefs?.current[selectedColumn], _right = true) => {
        const i = findIndex();

        const _placeholder = {
            value: 1, right: _right, align: "right", label: "", key: "blank", title: "New Column", width: chToPx(10), render: () => {
                return "---";
            }
        };
        let _columns = [...draftColumns];

        if (_right) {
            _columns.splice(i + 1, 0, _placeholder);
            setDraftColumns(_columns);
        } else {
            _columns.splice(i, 0, _placeholder);
            setDraftColumns(_columns)
        }
        setColumnsChanged(true);
        setSelectedColumn("blank");



    }

    useEffect(() => {

        let _f = draftColumns.find(item => item.key === "blank")

        if (!_f) {
            return;
        }


        let _right = _f.right;
        let _ref = columnRefs?.current["blank"];



        drawPicker({
            controller: controller,
            value: 0, title: "Insert Column", closeable: false, showSearch: true, mask_close: true, height: 280, ref: _ref, route: ROUTES.REPORTS.COLUMNS, close: (e) => {

                let _f = draftColumns.findIndex(item => item.key === "blank");
                if (_f > 0) {

                    setDraftColumns(draftColumns.filter(item => item.key !== "blank"));
                    setSelectedColumn(draftColumns[_f - 1].key);
                    window.bm_state.value = route;
                }

            }, callback: (e) => {
                if (e) {

                    if (draftColumns.find(i => i.value === e)) {
                        showError({ error: "Already exists." })
                    } else {
                        showMessage("Column added.")
                        addColumn(e, _right);
                    }

                } else {
                    let _f = draftColumns.findIndex(item => item.key === "blank");
                    if (_f > 0) {

                        setDraftColumns(draftColumns.filter(item => item.key !== "blank"));
                        setSelectedColumn(draftColumns[_f - 1].key);
                        window.bm_state.value = route;
                    }

                }
            }, offset: [-370, 0],
            options: availableColumns.sort((a, b) => a.label.localeCompare(b.label)).map(item => {
                if (draftColumns.find(i => i.value === item.value)) {
                    return { ...item, added: true, checked: true, label: item.label }
                } else {
                    return item;
                }
            })
        })


    }, [draftColumns])

    const handleContextMenu = (_ref, _value = "") => {

        if (window.bm_state.value !== route) { return; }
        drawPicker({
            controller: controller,
            value: "insert", title: "", closeable: true, mask_close: true, height: 300, ref: _ref, route: route, close: () => { }, callback: (e) => {
                if (e) {
                    if (e === "delete") {
                        removeColumn();
                    }
                    if (e === "insert_right") {

                        setTimeout(() => { drawInsert(_ref, true) }, 10)
                    }
                    if (e === "insert_left") {
                        setTimeout(() => { drawInsert(_ref, false) }, 10)
                    }
                }
            }, offset: [-160, 0],
            options: [
                { value: "insert_right", label: "Insert column to the right..." },
                { value: "insert_left", label: "Insert column to the left..." },
                { value: "delete", label: <span style={{ "color": "var(--text-red)" }}>Delete column</span> },
                { value: "", label: "Cancel" },
            ]
        })
    };





    const totalWidth = draftColumns.reduce((sum, col) => sum + (Math.ceil(col.width) || 0), 0);

    const columnRefs = useRef([]);







    const onColumnResize = (_column, _delta, _width) => {
        if (_column !== selectedColumn) return;

        let updatedColumns;
        const amt = Math.round((_width + _delta) / 9.3466);

        setDraftColumns(cols => {
            updatedColumns = cols.map(c => {
                if (c.key === selectedColumn) {
                    const minWidth = chToPx(1);
                    return {
                        ...c,
                        width: Math.max(chToPx(amt), minWidth)
                    };
                }
                return c;
            });
            return updatedColumns;
        });

        setColumnsChanged(true);

        // debouncedPurge(updatedColumns);
    };


    return (
        <div className="no-focus" ref={ref} tabIndex={0} style={{ width: "100%", "height": "100%" }}>
            <Spin style={{ "height": "100%" }} loading={loading}>
                <div className={(edit) ? "no-focus" : "desaturate"} style={{ "height": "100%" }} >
                    <ScrollWrap columnView={columnView} ref={scrollRef} width={totalWidth} >
                        <VirtualTable
                            controller={controller}
                            empty="No Results"
                            style={{ "minWidth": "100%" }}
                            ref={columnRefs}
                            onColumnResize={onColumnResize}
                            onColDoubleClick={() => drawSwap()}
                            reorderColumn={(selected, target) => swapColumn(target)}
                            onContext={(e) => handleContextMenu(e)}
                            selectedColumn={selectedColumn}
                            setSelectedColumn={(e) => {
                                onFocus();
                                setSelectedColumn(e)
                            }}
                            onColumnPositionChange={setColumnPosition}
                            // edit={edit}
                            // small={small}
                            // showColumnHeaders={showColumnHeaders}
                            // header={<Float><Label className="light" style={{ "textAlign": "left" }}>{header}</Label>{(show_add && <a onClick={() => kbdEvent(KBD.N)} style={{ "fontSize": "14px" }}>Add Filter +</a>)}</Float>}
                            // headerClass={headerClass}
                            // onRowClick={(item, event, index) => { setAlign("auto"); setSelected(item) }}
                            // next={next}
                            // prev={prev}
                            // align={align}
                            row_key={row_key}
                            current={{ column_id: "not" }}
                            data={(edit) ? previewData() : data}
                            columns={(edit) ? draftColumns : columns}
                            width={totalWidth}
                            loading={loading}
                            // offset={0}
                            // height={200}
                            fullHeight={true}


                        // route={(edit) ? ROUTES.REPORTS.FILTER : ""}
                        />
                    </ScrollWrap>
                </div>
            </Spin>

            <div className="shim" /><div className="shim" /><div className="shim" style={{ "height": "20px" }} />
            {(edit &&
                <div style={{ 'height': "30px" }}>
                    <Space style={{ "padding": "0px 0px", "marginLeft": "-5px" }}>
                        {(edit && <Nav nav={nav} />)}
                    </Space>
                </div>
            )}

        </div>
    )
})
export default ReportColumns;